import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import Cookies from 'js-cookie';

const wpAdminUrl = process.env.REACT_APP_WP_ADMIN_URI;
const wpJsonUrl = process.env.REACT_APP_WP_JSON_URI;
const aiUrl = window.AI_API_URL || process.env.REACT_APP_AI_BASE_URI;

// Create a new Axios instance with default settings
const apiClient: AxiosInstance = axios.create({
  baseURL: window.baseUri,
});

// Add an interceptor for responses
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: any) => {
    // Handle errors
    if (error.response) {
      const { status, data } = error.response;
      const message = data?.message || 'An error occurred';
      return Promise.reject({ status, message });
    } else {
      return Promise.reject({ status: 500, message: 'An error occurred' });
    }
  }
);

/**
 * Adds an authorization token to the Axios request configuration if `useToken` is true.
 * The token is retrieved from a cookie named "n_token".
 * @param config - The Axios request configuration to add the token to.
 * @param useToken - Whether to add the token to the configuration or not.
 * @returns The updated Axios request configuration.
 */
const addTokenToConfig = (config?: AxiosRequestConfig, useToken?: boolean): AxiosRequestConfig => {
  if (useToken) {
    config = config || {};
    config.headers = config.headers || {};

    const token = Cookies.get('n_token');
    if (token) {
      config.headers.Authorization = `Basic ${btoa('letMe:in')},${token}`;
    }
  }

  return config || {};
};

/**
 * Makes a GET request to the specified endpoint with optional token authentication.
 * @param endpoint - The endpoint to request.
 * @param useToken - Whether to use token authentication.
 * @param config - Optional Axios request configuration.
 * @returns A Promise that resolves to the AxiosResponse object.
 */
export const get = async <T>(
  endpoint: string,
  useToken?: boolean,
  config?: AxiosRequestConfig,
  extraParams?: object,
  withCache?: number
): Promise<AxiosResponse<T>> => {
  const cacheBuster = withCache && withCache > 0 ? { withCache: withCache } : {};

  return await apiClient.get<T>(
    wpAdminUrl + '',
    addTokenToConfig(
      {
        params: {
          action: 'remp_get',
          endpoint,
          ...extraParams,
          ...cacheBuster,
        },
        ...config,
      },
      useToken
    )
  );
};

export const getWp = async <T>(endpoint: string): Promise<AxiosResponse<T>> => {
  return await apiClient.get<T>(wpJsonUrl + endpoint);
};

/**
 * Makes a GET request to the AI endpoint with the specified endpoint.
 * @param endpoint - The endpoint to make the request to.
 * @returns A Promise that resolves with the AxiosResponse of type T.
 */
export const getAiRequest = async <T>(endpoint: string): Promise<AxiosResponse<T>> => {
  return await apiClient.get<T>(aiUrl + endpoint);
};

/**
 * Sends a POST request to the AI endpoint with the provided data.
 * @param endpoint - The endpoint to send the request to.
 * @param data - The data to send with the request.
 * @param config - Optional Axios request configuration.
 * @returns A Promise that resolves with the AxiosResponse of type T.
 */
export const postAiRequest = async <T>(
  endpoint: string,
  data: any,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return await apiClient.post<T>(`${aiUrl}/${endpoint}`, data, {
    data,
    ...config,
  });
};

/**
 * Sends a POST request to the specified endpoint with the given data.
 * @param endpoint - The endpoint to send the request to.
 * @param useToken - Whether or not to include the user's token in the request.
 * @param data - The data to be sent in the request body.
 * @param config - Optional AxiosRequestConfig object to configure the request.
 * @returns A Promise that resolves to an AxiosResponse object containing the response data.
 */
export const post = async <T>(
  endpoint: string,
  useToken?: boolean,
  data?: any,
  config?: AxiosRequestConfig,
  extraParams?: object
): Promise<AxiosResponse<T>> => {
  return await apiClient.post<T>(
    wpAdminUrl + '',
    data,
    addTokenToConfig(
      {
        params: {
          action: 'remp_post',
          endpoint,
          ...extraParams,
        },
        ...config,
      },
      useToken
    )
  );
};

export const postWpAjax = async <T>(
  action: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return await apiClient.post<T>(wpAdminUrl + '', data, {
    params: { action },
    withCredentials: true,
    ...config,
  });
};

export const getFromCrm = async <T>(endpoint: string, params?: object): Promise<AxiosResponse<T>> => {
  const baseUrl = window.crmUrl;

  if (!baseUrl) {
    console.error('CRM base URL is not defined');
    return Promise.reject({
      status: 500,
      message: 'CRM base URL is not defined',
    });
  }

  const ecommerceUrl = baseUrl + '/api/v1';
  return await apiClient.get<T>(ecommerceUrl + endpoint, { params });
};
